import type {
  PanoramaHotspot,
  ViewerEntityId,
} from './contracts/models';

export const VIEWER_DEEP_LINK_PARAMS = {
  layout: 'layout',
  photo: 'photo',
  hotspot: 'hotspot',
  pitch: 'pitch',
  yaw: 'yaw',
} as const;

export interface ViewerDeepLinkCoordinates {
  pitch: number;
  yaw: number;
}

export interface ViewerDeepLinkTarget {
  layoutId?: string;
  photoId?: number;
  hotspotId?: number;
  coordinates?: ViewerDeepLinkCoordinates;
}

export interface ParsedViewerDeepLink
  extends ViewerDeepLinkTarget {
  /**
   * Stable identity containing only viewer-owned query parameters.
   * Hosts can use this to apply a deep link once without reacting to
   * unrelated application query-string changes.
   */
  key: string;
}

export type ViewerDeepLinkSearchInput =
  | string
  | URLSearchParams;

const toSearchParams = (
  input: ViewerDeepLinkSearchInput,
): URLSearchParams =>
  input instanceof URLSearchParams
    ? new URLSearchParams(input)
    : new URLSearchParams(
        input.startsWith('?') ? input.slice(1) : input,
      );

const parseFiniteNumber = (
  value: string | null,
): number | undefined => {
  if (value == null || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const setFiniteNumber = (
  params: URLSearchParams,
  key: string,
  value: number | undefined,
): void => {
  if (value !== undefined && Number.isFinite(value)) {
    params.set(key, String(value));
  }
};

/**
 * Creates the canonical viewer-owned portion of a query string.
 *
 * Unknown host parameters from `base` are preserved. Existing viewer
 * parameters are replaced by `target`, which also makes this useful when
 * generating shareable URLs from the current viewer state.
 */
export const createViewerDeepLinkSearchParams = (
  target: ViewerDeepLinkTarget,
  base: ViewerDeepLinkSearchInput = '',
): URLSearchParams => {
  const params = toSearchParams(base);
  Object.values(VIEWER_DEEP_LINK_PARAMS).forEach((key) => {
    params.delete(key);
  });

  const layoutId = target.layoutId?.trim();
  if (layoutId) {
    params.set(VIEWER_DEEP_LINK_PARAMS.layout, layoutId);
  }
  setFiniteNumber(
    params,
    VIEWER_DEEP_LINK_PARAMS.photo,
    target.photoId,
  );
  setFiniteNumber(
    params,
    VIEWER_DEEP_LINK_PARAMS.hotspot,
    target.hotspotId,
  );
  if (
    target.coordinates &&
    Number.isFinite(target.coordinates.pitch) &&
    Number.isFinite(target.coordinates.yaw)
  ) {
    params.set(
      VIEWER_DEEP_LINK_PARAMS.pitch,
      String(target.coordinates.pitch),
    );
    params.set(
      VIEWER_DEEP_LINK_PARAMS.yaw,
      String(target.coordinates.yaw),
    );
  }

  return params;
};

export const parseViewerDeepLink = (
  input: ViewerDeepLinkSearchInput,
): ParsedViewerDeepLink => {
  const params = toSearchParams(input);
  const layoutId =
    params.get(VIEWER_DEEP_LINK_PARAMS.layout)?.trim() ||
    undefined;
  const photoId = parseFiniteNumber(
    params.get(VIEWER_DEEP_LINK_PARAMS.photo),
  );
  const hotspotId = parseFiniteNumber(
    params.get(VIEWER_DEEP_LINK_PARAMS.hotspot),
  );
  const pitch = parseFiniteNumber(
    params.get(VIEWER_DEEP_LINK_PARAMS.pitch),
  );
  const yaw = parseFiniteNumber(
    params.get(VIEWER_DEEP_LINK_PARAMS.yaw),
  );
  const coordinates =
    pitch !== undefined && yaw !== undefined
      ? { pitch, yaw }
      : undefined;
  const target = {
    layoutId,
    photoId,
    hotspotId,
    coordinates,
  };

  return {
    ...target,
    key: createViewerDeepLinkSearchParams(target).toString(),
  };
};

/**
 * Removes one-shot panorama navigation while retaining layout selection and
 * any host-owned query parameters.
 */
export const clearViewerPanoramaDeepLinkParams = (
  input: ViewerDeepLinkSearchInput,
): URLSearchParams => {
  const params = toSearchParams(input);
  [
    VIEWER_DEEP_LINK_PARAMS.photo,
    VIEWER_DEEP_LINK_PARAMS.hotspot,
    VIEWER_DEEP_LINK_PARAMS.pitch,
    VIEWER_DEEP_LINK_PARAMS.yaw,
  ].forEach((key) => {
    params.delete(key);
  });
  return params;
};

/**
 * Resolves a URL layout identifier to the host's canonical identifier.
 * An unresolved layout is omitted, matching the existing Ironview 360
 * behavior while still allowing a photo-only target to open.
 */
export const resolveViewerDeepLinkLayoutId = (
  layoutId: string | undefined,
  availableLayoutIds: readonly ViewerEntityId[],
): string | undefined => {
  if (!layoutId) return undefined;
  const match = availableLayoutIds.find(
    (candidate) => String(candidate) === layoutId,
  );
  return match === undefined ? undefined : String(match);
};

export const findViewerDeepLinkHotspotCoordinates = (
  hotspotId: number | undefined,
  hotspots: readonly PanoramaHotspot[],
): ViewerDeepLinkCoordinates | undefined => {
  if (hotspotId === undefined) return undefined;
  const hotspot = hotspots.find(
    (candidate) => Number(candidate.id) === hotspotId,
  );
  return hotspot
    ? { pitch: hotspot.pitch, yaw: hotspot.yaw }
    : undefined;
};
